import type { ITrack } from "@designcombo/types";
import type {
	CaptionWordEffectsType,
	ClipOptionsStyleSchemaType,
} from "@repo/types";

export const CAPTION_WORD_EFFECT_IDS = {
	jellyWobble: "jellyWobble",
} as const;

export type CaptionWordEffectId =
	(typeof CAPTION_WORD_EFFECT_IDS)[keyof typeof CAPTION_WORD_EFFECT_IDS];

export interface CaptionWordEffectDefinition {
	id: CaptionWordEffectId;
	label: string;
	description: string;
}

export const CAPTION_WORD_EFFECT_CATALOG: CaptionWordEffectDefinition[] = [
	{
		id: CAPTION_WORD_EFFECT_IDS.jellyWobble,
		label: "Jelly wobble",
		description: "Squash and stretch the spoken word",
	},
];

export interface CaptionTrackFields extends ITrack {
	styles?: ClipOptionsStyleSchemaType[];
	maxWordsInPage?: number;
	wordEffects?: CaptionWordEffectsType;
}

const EMPTY_CAPTION_WORD_EFFECTS: CaptionWordEffectsType = {};

export function getCaptionWordEffects(
	track?: ITrack | null,
): CaptionWordEffectsType {
	return (
		(track as CaptionTrackFields | undefined)?.wordEffects ??
		EMPTY_CAPTION_WORD_EFFECTS
	);
}

export function hasCaptionWordEffectsConfig(
	effects?: CaptionWordEffectsType,
): boolean {
	if (!effects) return false;
	return Object.keys(effects).length > 0;
}

export function firstCaptionWordEffects(
	...sources: Array<CaptionWordEffectsType | undefined | null>
): CaptionWordEffectsType | undefined {
	for (const source of sources) {
		if (source && hasCaptionWordEffectsConfig(source)) return source;
	}
	return undefined;
}

export function captionWordEffectsPayload(
	jellyEnabled: boolean,
): CaptionWordEffectsType | undefined {
	if (!jellyEnabled) return undefined;
	return {
		jellyWobble: { enabled: true },
	};
}

export function isCaptionWordEffectEnabled(
	effects: CaptionWordEffectsType | undefined,
	id: CaptionWordEffectId,
): boolean {
	return effects?.[id]?.enabled === true;
}

export function setCaptionWordEffectEnabled(
	effects: CaptionWordEffectsType | undefined,
	id: CaptionWordEffectId,
	enabled: boolean,
): CaptionWordEffectsType {
	const current = effects?.[id];
	return {
		...effects,
		[id]: {
			...current,
			enabled,
			intensity: current?.intensity ?? 1,
		},
	};
}

export function patchCaptionTrack(
	tracks: ITrack[],
	trackId: string,
	patch: Partial<CaptionTrackFields>,
): ITrack[] {
	return tracks.map((track) => {
		if (track.id !== trackId) return track;
		return {
			...track,
			...patch,
		};
	});
}

export function applyCaptionBrandFields(
	track: ITrack,
	options: {
		maxWordsInPage?: number | null;
		style?: ClipOptionsStyleSchemaType[] | null;
		wordEffects?: CaptionWordEffectsType | null;
	},
): CaptionTrackFields {
	const next = { ...(track as CaptionTrackFields) };
	if (options.maxWordsInPage === null) delete next.maxWordsInPage;
	else if (options.maxWordsInPage !== undefined)
		next.maxWordsInPage = options.maxWordsInPage;
	if (options.style === null) delete next.styles;
	else if (options.style !== undefined) next.styles = options.style;
	if (options.wordEffects === null) delete next.wordEffects;
	else if (options.wordEffects !== undefined)
		next.wordEffects = options.wordEffects;
	return next;
}

export interface CaptionWordEffectMotion {
	scaleX: number;
	scaleY: number;
	rotate: number;
}

export function resolveCaptionWordEffectMotion(options: {
	effects?: CaptionWordEffectsType;
	isActive: boolean;
	wordFrame: number;
	fps: number;
}): CaptionWordEffectMotion | null {
	if (!options.isActive) return null;

	const jelly = options.effects?.jellyWobble;
	if (!jelly?.enabled) return null;

	const intensity =
		typeof jelly.intensity === "number" && Number.isFinite(jelly.intensity)
			? jelly.intensity
			: 1;
	const t = options.wordFrame / Math.max(1, options.fps);
	const wobble = Math.sin(t * Math.PI * 8);

	return {
		scaleX: 1 + 0.18 * intensity * wobble,
		scaleY: 1 - 0.14 * intensity * wobble,
		rotate: 5 * intensity * Math.sin(t * Math.PI * 5),
	};
}

export function captionWordTransformCss(options: {
	translateX: number;
	translateY: number;
	scaleX: number;
	scaleY: number;
	rotate: number;
}): string {
	return `translate(${options.translateX}px, ${options.translateY}px) scale(${options.scaleX}, ${options.scaleY}) rotate(${options.rotate}deg)`;
}

export function mergeCaptionWordStyle(
	style: Record<string, unknown>,
	opacity: number,
	transform: string,
	wordEffectMotion: CaptionWordEffectMotion | null,
): Record<string, unknown> {
	return {
		opacity,
		transform,
		...style,
		...(wordEffectMotion ? { transform } : {}),
	};
}
